import React from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import { ONE_MASTER_FLAG, DATE_TIME_FORMAT } from '../../utilities/constants';
import { messageUtility } from '../../utilities/messageUtility';
import Tippy from '../ReactTippy';

import { AgencyCompaniesIcon } from '../../components/icons/AgencyCompaniesIcon.jsx';
import { StatusTag } from '../../components/StatusTag';

/**
 * tooltip表示判定
 * @param {object} instance // tippy
 */
const setTippyHide = instance => {
  const showingTooltipLength = 8;
  instance.popper.hidden =
    instance.options.html.innerText.length < showingTooltipLength;
};

/**
 * 協力会社一覧テーブル部分
 * @param data {object} 一覧情報
 * @param callbacks {func}
 */
export const AgencyCompanyContents = ({ data, callbacks }) => {
  const styles = {
    icon: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    tableTooltipRow: {
      maxWidth: 180,
    },
  };
  const renderWorkerTooltip = company => {
    const messages = [];
    if (company.has_expired_workers) {
      messages.push(messageUtility.getMessage('E-001-105'));
    }
    if (company.has_about_to_expire_workers) {
      messages.push(messageUtility.getMessage('E-001-106'));
    }
    if (company.has_foreign_construction_workers) {
      messages.push(messageUtility.getMessage('E-001-107'));
    }
    return (
      <span className='darklist__tooltip'>
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            {messages.length > 1 && '・'}
            {message}
            {index < messages.length - 1 && <br />}
          </React.Fragment>
        ))}
      </span>
    );
  };
  return (
    <tbody>
      {_.map(data, company => {
        const hasCompanyExpiration =
          _.some([
            company.has_about_to_expire_company,
            company.has_expired_company,
          ]) && company.status !== 'revoked';
        const hasWorkersExpiration =
          _.some([
            company.has_about_to_expire_workers,
            company.has_expired_workers,
          ]) && company.status !== 'revoked';
        const hasForeignConstructionWorkers =
          _.some([company.has_foreign_construction_workers]) &&
          company.status !== 'revoked';
        return (
          <tr
            style={{ height: '60px' }}
            className={
              company.status !== 'revoked'
                ? 'table__item'
                : 'table__item AgencyCompanies__table-item--revoked'
            }
            key={company.id}
          >
            <td className='value-table' style={styles.tableTooltipRow}>
              <span className='label text-hidden'>代行先会社名</span>

              {/* tooltip */}
              <Tippy
                duration={10}
                distance={24}
                arrowType='sharp'
                delay={[500, 0]}
                placement='bottom'
                followCursor={true}
                html={
                  <div className='data-table__tooltip'>
                    {company.name ? company.name : ''}
                  </div>
                }
                onShow={setTippyHide}
              >
                <div className='value-table__company-name'>
                  {company.name ? company.name : '-'}
                </div>
              </Tippy>
            </td>
            <td className='value-table' style={styles.tableTooltipRow}>
              <span className='label text-hidden'>住所</span>
              <Tippy
                duration={10}
                distance={24}
                arrowType='sharp'
                delay={[500, 0]}
                placement='bottom'
                followCursor={true}
                html={
                  <div className='data-table__tooltip'>{company.address}</div>
                }
                onShow={setTippyHide}
              >
                <div className='value-table__company-name'>
                  {company.address ? company.address : '-'}
                </div>
              </Tippy>
            </td>
            <td className='value-table'>
              <span className='label text-hidden'>電話番号</span>
              <div className='value-table__tel'>
                {company.tel ? company.tel : '-'}
              </div>
            </td>
            <td className='value-table'>
              <span className='label text-hidden'>代行開始日</span>
              <div className='value-table__date'>
                {company.approved_at
                  ? moment(company.approved_at, 'YYYY/MM/DD HH:mm:ss').format(
                      'YYYY/MM/DD'
                    )
                  : '-'}
              </div>
            </td>
            <td className='value-table AgencyCompanies__table-cell'>
              <div className='AgencyCompanies__table-cell-box'>
                <Tippy
                  duration={10}
                  distance={24}
                  arrow='true'
                  arrowType='sharp'
                  delay={[50, 100]}
                  placement='bottom'
                  followCursor={true}
                  html={
                    <span className='darklist__tooltip'>
                      {company.has_expired_company
                        ? messageUtility.getMessage('E-001-105')
                        : company.has_about_to_expire_company
                        ? messageUtility.getMessage('E-001-106')
                        : null}
                    </span>
                  }
                  disable={!hasCompanyExpiration}
                >
                  <button
                    className='btn btn--large-icon AgencyCompanies__button'
                    disabled={company.status === 'revoked'}
                    onMouseDown={() => callbacks.table.edit(company)}
                  >
                    <i className='i-company' />
                    会社情報
                    {hasCompanyExpiration && (
                      <span
                        className={
                          company.has_expired_company
                            ? `AgencyCompanies__badge AgencyCompanies__badge--warning`
                            : company.has_about_to_expire_company
                            ? `AgencyCompanies__badge AgencyCompanies__badge--caution`
                            : null
                        }
                      >
                        <i className='icon-error_round' />
                      </span>
                    )}
                  </button>
                </Tippy>
                <Tippy
                  duration={10}
                  distance={24}
                  arrow='true'
                  arrowType='sharp'
                  delay={[50, 100]}
                  placement='bottom'
                  followCursor={true}
                  html={renderWorkerTooltip(company)}
                  disable={
                    !hasWorkersExpiration && !hasForeignConstructionWorkers
                  }
                >
                  <button
                    className='btn btn--large-icon AgencyCompanies__button'
                    disabled={company.status === 'revoked'}
                    onMouseDown={() => callbacks.table.list(company)}
                  >
                    <i className='i-workers' />
                    作業員
                    {(hasWorkersExpiration ||
                      hasForeignConstructionWorkers) && (
                      <span
                        className={
                          company.has_expired_workers
                            ? `AgencyCompanies__badge AgencyCompanies__badge--warning`
                            : company.has_foreign_construction_workers
                            ? `AgencyCompanies__badge AgencyCompanies__badge--warning`
                            : company.has_about_to_expire_workers
                            ? `AgencyCompanies__badge AgencyCompanies__badge--caution`
                            : null
                        }
                      >
                        <i className='icon-error_round' />
                      </span>
                    )}
                  </button>
                </Tippy>
                <button
                  className='btn btn--large-icon'
                  disabled={company.status === 'revoked'}
                  onMouseDown={() => callbacks.table.agency(company)}
                >
                  <i className='i-group-add' />
                  代行担当
                </button>
              </div>
            </td>
            <td className='value-table'>
              <span className='label text-hidden'>代行関係解除</span>
              {company.status !== 'revoked' ? (
                <div className='AgencyCompanies__table-cell-box'>
                  <button
                    className='btn--negative'
                    onMouseDown={() => callbacks.table.revoke(company)}
                  >
                    代行関係を解除
                  </button>
                </div>
              ) : (
                <div className='AgencyCompanies__table-cell-box'>
                  <StatusTag type='suspension'>
                    {company.revoked_at !== null ? '解除済' : '失効'}
                  </StatusTag>
                  <div>
                    <p>
                      解除日：
                      {company.revoked_at !== null
                        ? moment(
                            company.revoked_at,
                            DATE_TIME_FORMAT.slashDateTimeSecondFormat
                          ).format(DATE_TIME_FORMAT.slashDateFormat)
                        : ''}
                    </p>
                    <p>解除者：{company.revoked_user_name}</p>
                  </div>
                </div>
              )}
            </td>
          </tr>
        );
      })}
    </tbody>
  );
};



// WEBPACK FOOTER //
// ./src/components/tables/AgencyCompaniesTable.jsx